import { useDisplayName } from '@/composables/useDisplayName';
import { EQuickEntryCommandTrigger } from '@/composables/quickEntry/quickEntry.enums';
import { consumeCommandToken, parseCommandToken, type ICommandToken } from '@/composables/quickEntry/quickEntry.utils';
import type { TStudentLike } from '@/interfaces';
import { EKeyboardKey } from '@/utils/menu.utils';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export enum ESupportMeasureProtocolSuggestionKind {
  OpenStudents = 'openStudents',
  OpenCompetences = 'openCompetences',
  OpenMaterials = 'openMaterials',
  Student = 'student',
}

export interface ISupportMeasureProtocolSuggestionItem {
  id: string;
  label: string;
  kind: ESupportMeasureProtocolSuggestionKind;
  student?: TStudentLike;
}

interface ISupportMeasureProtocolSuggestionSources {
  canSelectCompetences: Ref<boolean>;
  selectedStudentIds: Ref<number[]>;
  students: Ref<TStudentLike[]>;
}

interface ISupportMeasureProtocolSuggestionActions {
  addStudent: (studentId: number) => void;
}

interface ISupportMeasureProtocolSuggestionsComposable {
  isOpen: ComputedRef<boolean>;
  items: ComputedRef<ISupportMeasureProtocolSuggestionItem[]>;
  activeIndex: Ref<number>;
  activeToken: Ref<ICommandToken | null>;
  updateFromInput: (text: string, caretIndex: number) => void;
  handleSuggestionKeydown: (event: KeyboardEvent) => boolean;
  acceptItem: (item: ISupportMeasureProtocolSuggestionItem) => { text: string; caretIndex: number } | null;
  close: () => void;
}

export const useSupportMeasureProtocolSuggestions = (
  sources: ISupportMeasureProtocolSuggestionSources,
  actions: ISupportMeasureProtocolSuggestionActions,
  options: { enabled?: Ref<boolean> } = {},
): ISupportMeasureProtocolSuggestionsComposable => {
  const { t } = useI18n();
  const { getStudentDisplayName } = useDisplayName();
  const enabled = computed(() => options.enabled?.value ?? true);

  const activeToken = ref<ICommandToken | null>(null);
  const activeIndex = ref(0);
  const currentText = ref('');
  const currentCaretIndex = ref(0);

  const commandItems = computed<ISupportMeasureProtocolSuggestionItem[]>(() => [
    {
      id: 'open-students',
      label: t('quickEntry.commandOpenStudents'),
      kind: ESupportMeasureProtocolSuggestionKind.OpenStudents,
    },
    ...(sources.canSelectCompetences.value
      ? [
          {
            id: 'open-competences',
            label: t('quickEntry.commandOpenCompetences'),
            kind: ESupportMeasureProtocolSuggestionKind.OpenCompetences,
          } as const,
        ]
      : []),
    {
      id: 'open-materials',
      label: t('supportMeasureEntry.materials.commandOpen'),
      kind: ESupportMeasureProtocolSuggestionKind.OpenMaterials,
    },
  ]);

  const studentItems = computed<ISupportMeasureProtocolSuggestionItem[]>(() => {
    const selectedIds = new Set(sources.selectedStudentIds.value);
    return sources.students.value
      .filter((student) => !selectedIds.has(student.id))
      .map((student) => ({
        id: `student-${student.id}`,
        label: getStudentDisplayName(student),
        kind: ESupportMeasureProtocolSuggestionKind.Student,
        student,
      }));
  });

  const items = computed<ISupportMeasureProtocolSuggestionItem[]>(() => {
    const token = activeToken.value;
    if (!token) return [];

    const source = token.trigger === EQuickEntryCommandTrigger.Student ? studentItems.value : commandItems.value;
    const query = token.query.trim();
    return query ? source.filter((item) => matchesAllSearchWords([item.label], query)) : source;
  });

  const isOpen = computed<boolean>(() => activeToken.value !== null && items.value.length > 0);
  const close = (): void => {
    activeToken.value = null;
    activeIndex.value = 0;
  };

  watch(
    enabled,
    (isEnabled) => {
      if (!isEnabled) close();
    },
    { flush: 'sync' },
  );

  const updateFromInput = (text: string, caretIndex: number): void => {
    currentText.value = text;
    currentCaretIndex.value = caretIndex;
    const token = enabled.value ? parseCommandToken(text, caretIndex) : null;
    if (!token) {
      close();
      return;
    }

    if (!activeToken.value || activeToken.value.startIndex !== token.startIndex) activeIndex.value = 0;
    activeToken.value = token;
    if (activeIndex.value >= items.value.length) activeIndex.value = 0;
  };

  const acceptItem = (item: ISupportMeasureProtocolSuggestionItem): { text: string; caretIndex: number } | null => {
    const token = activeToken.value;
    if (!token) return null;
    const consumed = consumeCommandToken(currentText.value, currentCaretIndex.value, token);
    if (item.kind === ESupportMeasureProtocolSuggestionKind.Student && item.student) {
      actions.addStudent(item.student.id);
    }
    close();
    currentText.value = consumed.text;
    currentCaretIndex.value = consumed.caretIndex;
    return consumed;
  };

  const handleSuggestionKeydown = (event: KeyboardEvent): boolean => {
    if (!isOpen.value) return false;
    const total = items.value.length;
    if (event.key === EKeyboardKey.Down) {
      event.preventDefault();
      activeIndex.value = (activeIndex.value + 1) % total;
      return true;
    }
    if (event.key === EKeyboardKey.Up) {
      event.preventDefault();
      activeIndex.value = (activeIndex.value - 1 + total) % total;
      return true;
    }
    if (event.key === EKeyboardKey.Escape) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return true;
    }
    return false;
  };

  return {
    isOpen,
    items,
    activeIndex,
    activeToken,
    updateFromInput,
    handleSuggestionKeydown,
    acceptItem,
    close,
  };
};
